/**
 * Primary state move for one list row or board card.
 *
 * Follows `taskMachine.eventsFrom` (Claim / Start / Complete / Send for review /
 * Approve and close), same ownership rule as `Lifecycle` (#37): work moves hide when
 * somebody else holds the task — except "Assign to me", which is how a reader takes
 * one that is not theirs (#6).
 *
 * The rare moves — comment, log time, block, give it back, cancel — are
 * `TaskMoreMenu`'s, beside the primary on every row and card (#5).
 */

import { taskMachine, type TaskEvent, type TaskView } from '@fareast/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { asOnePress } from '../../../api/hooks.js'
import { messageOf } from '../../../api/problem.js'
import { reviewOf, useStudioAction, type StudioActionName } from '../../../api/studio.js'
import { usePrincipal } from '../../../auth/principal.js'
import { Button } from '../../../components/portal/Button.js'
import { ActionDialog } from '../../../components/studio/ActionDialog.js'
import { stampOf, statusOf, useOwnMoveOnItsWay } from '../../../components/studio/CellEdit.js'
import { NeedsReviewNotice } from '../../../components/studio/NeedsReviewNotice.js'
import { TaskMoreMenu } from '../../../components/studio/TaskMoreMenu.js'
import {
  heldAs,
  holdsUnmadeCall,
  moveLabel,
  moveOf,
  moveWindowOf,
  overtaken,
} from '../../../components/studio/taskWords.js'
import { canMutateWorkOf } from '../taskDetail/Lifecycle.js'
import { movedLabel } from './fieldSave.js'
import {
  unlessChanged,
  unlessChangedBeforeStart,
  unlessTaken,
  useTaskNotice,
  type TaskUndo,
} from './taskNotice.js'

type PrimaryKind =
  | { readonly kind: 'claim'; label: string }
  | { readonly kind: 'start'; label: string }
  | {
      readonly kind: 'dialog'
      readonly action: StudioActionName
      readonly label: string
      readonly title: string
    }

export type RowActionsProps = { readonly task: TaskView }

export function RowActions({ task }: RowActionsProps) {
  const principal = usePrincipal()
  const claim = useStudioAction('tasks_claim')
  const start = useStudioAction('tasks_start')
  const assign = useStudioAction('tasks_assign')
  const release = useStudioAction('tasks_release')
  const notice = useTaskNotice()
  const queryClient = useQueryClient()
  /*
   * Everything one press on this row makes, on its way as one (`api/hooks.ts`
   * `asOnePress`): a take is up to two writes, "Claim & start" up to three, and the
   * way back as many again.
   */
  const asOne = <T,>(steps: () => Promise<T>): Promise<T> =>
    asOnePress(queryClient, { task_ref: task.ref }, steps)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<unknown>(null)

  const events = taskMachine.eventsFrom(task.status)
  const finished = taskMachine.isTerminal(task.status)
  const meId = principal.kind === 'staff_user' ? principal.userId : null
  const canMutateWork = canMutateWorkOf(
    task,
    principal.kind === 'staff_user' ? principal.userId : null,
  )

  /*
   * Approving a review is somebody else's move by construction — `submit_for_review`
   * keeps the assignee — so it is the one move that survives the holder rule.
   */
  const primary = finished
    ? null
    : primaryOf(
        task,
        canMutateWork ? events : events.filter((one) => one === 'approve_review'),
        meId,
      )
  /*
   * A move of the reader's own on this task from **anywhere** — the Status cell or the
   * Who cell beside these buttons, the page's Undo — and not only this row's own
   * (`CellEdit.tsx` `useOwnMoveOnItsWay`): each is made from what the row says, and
   * the row does not say what that move leaves until it has been read again.
   */
  const moving = useOwnMoveOnItsWay(task)
  const pending =
    busy || moving || claim.isPending || start.isPending || assign.isPending || release.isPending

  /*
   * "Assign to me" and "Assign to me & start", one click each (#6 of the owner's
   * list). Taking a task used to mean opening Assign and finding your own name in a
   * grouped menu, and starting it meant a second trip.
   *
   * The chain is the state machine's, not a shortcut around it: an unassigned open
   * task is **claimed** (which takes the lease as well as the name), a task somebody
   * else holds is **assigned** and then claimed if the machine still allows it, and
   * `start` follows only where `start` is legal. Nothing here writes a status.
   */
  /*
   * "Eligible" is Ferit's own word, and his ownership line says what it means: a task
   * **no other staff member owns**. So the take buttons belong to an unassigned task
   * and to no other — a colleague's task is reassigned through the Who menu, which is
   * the control that already does it, and a task already yours needs no button that
   * says "give it to yourself".
   */
  const takeable = meId !== null && !finished && task.assignee_id === null
  const startable = takeable && (task.status === 'open' || task.status === 'claimed')

  /* The answer of its **last** write, which is the one whose stamp the Undo needs. */
  const take = async (): Promise<unknown> => {
    if (task.assignee_id === null && events.includes('claim')) {
      return claim.mutateAsync({ task_ref: task.ref })
    }
    const assigned = await assign.mutateAsync({
      task_ref: task.ref,
      assignee_kind: 'user',
      assignee_id: meId ?? '',
      // From what the row shows: nobody's, in this state. A task somebody else took a
      // moment ago is theirs, and "Assign to me" from an old row does not take it back.
      if_was: heldAs(task),
    })
    if (taskMachine.eventsFrom(task.status).includes('claim')) {
      return claim.mutateAsync({ task_ref: task.ref })
    }
    return assigned
  }

  /**
   * What puts this move back, given where the task stood before it and where the
   * move's own answer says it stands now.
   *
   * **A take that ended with the task claimed or under way** goes back by `release`
   * — the one move that both re-opens it and lets go of the name (`tasks/service.ts`
   * `release` sets `assigneeKind: 'none'`) — and then whatever was true before:
   * nothing, if nobody held it; the lease again, if the reader was already on it;
   * the previous holder otherwise. Nothing here writes a status: each step is a move
   * the machine already allows from where it leaves off.
   *
   * **A take that was a name and nothing else** goes back by taking the name off.
   * "Assign to me" on a task waiting in review cannot claim it — the machine has no
   * `claim` from `review` — so it only assigns, and `review` has no `release`
   * either. The Undo used to start with one regardless: refused every time, "A task
   * cannot release from review", with the way back gone and the task still the
   * reader's. `landed` is what tells the two apart, and it is the move's own answer
   * rather than a second copy of the machine's rule about which takes claim.
   *
   * **The first write carries the stamp, and only the first.** `since` is the stamp
   * the move's own last write answered with, and it rides on the first write of the
   * way back: an Undo pressed after the task was started, handed on or edited is
   * refused whole (`taskNotice.tsx` `unlessChanged`) instead of taking live work off
   * whoever has it now. The steps after it carry no stamp — the first one has just
   * moved it — and say instead what that write made true: a claim refuses itself over
   * somebody else's task, and the hand-back says "nobody's, and open".
   */
  const undoTake = (
    before: TaskStand,
    since: string,
    landed: TaskView['status'] | null,
  ): TaskUndo => {
    return () =>
      asOne(async () => {
        if (landed !== null && !taskMachine.can(landed, 'release')) {
          await unlessChanged(task.ref, () =>
            assign.mutateAsync({
              task_ref: task.ref,
              assignee_kind: before.assigneeKind,
              assignee_id: before.assigneeId,
              if_unchanged_since: since,
            }),
          )
          return
        }
        await unlessChanged(task.ref, () =>
          release.mutateAsync({ task_ref: task.ref, if_unchanged_since: since }),
        )
        if (before.assigneeId === null) return
        if (
          before.assigneeKind === 'user' &&
          before.assigneeId === meId &&
          before.status !== 'open'
        ) {
          await claim.mutateAsync({ task_ref: task.ref })
          return
        }
        await unlessTaken(task.ref, () =>
          assign.mutateAsync({
            task_ref: task.ref,
            assignee_kind: before.assigneeKind,
            assignee_id: before.assigneeId,
            // From what the give-back above has just made true: nobody's, and open. Taken
            // by somebody in between, it is theirs, and is not handed away from them.
            if_was: heldAs({ assignee_kind: 'none', assignee_id: null, status: 'open' }),
          }),
        )
      })
  }

  /** Where the task stands right now, for the line that offers to put it back. */
  const stand = (): TaskStand => ({
    status: task.status,
    assigneeKind: task.assignee_kind,
    assigneeId: task.assignee_id,
  })

  /**
   * One move, then one line about it.
   *
   * Claim, Start and the two takes changed the row and said nothing at all, which is
   * how a misclick on a board of thirty cards became unfindable. They now write the
   * page's own notice — the same line a cell edit writes, replacing it, so Undo is
   * never the step before last.
   *
   * The way back is built **from the move's own answer**, not before it: the stamp
   * `undoTake` sends is the one this write left on the task, and that exists only
   * once the write has come back. An answer with no stamp gets the sentence and no
   * Undo — one with nothing to send would be the unconditional release again.
   */
  const run = async (
    work: () => Promise<unknown>,
    said?: { readonly label: string; readonly before: TaskStand },
  ): Promise<void> => {
    setBusy(true)
    setResult(null)
    try {
      const answer = await asOne(work)
      setResult(answer)
      if (said !== undefined) {
        const since = stampOf(answer)
        notice.report({
          label: said.label,
          undo: since === null ? undefined : undoTake(said.before, since, statusOf(answer)),
          ref: task.ref,
        })
      }
    } catch (err) {
      notice.fail(overtaken(task.ref, err, 'move') ?? messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  /*
   * A task somebody else holds has no primary move — that is the ownership rule —
   * but "Assign to me" is exactly the button that case wants, so the early return
   * this function used to have would have hidden it where it matters most.
   */
  return (
    <div className="trow-actions">
      <div className="trow-actions__btns">
        <span className="trow-actions__main">
          {primary === null ? null : primary.kind === 'claim' ? (
            <Button
              size="sm"
              variant="primary"
              disabled={pending}
              onClick={() => {
                void run(() => claim.mutateAsync({ task_ref: task.ref }), {
                  label: movedLabel({ taskRef: task.ref, move: 'claim', landed: null }),
                  before: stand(),
                })
              }}
            >
              {claim.isPending ? 'Working…' : primary.label}
            </Button>
          ) : primary.kind === 'start' ? (
            <Button
              size="sm"
              variant="primary"
              disabled={pending}
              onClick={() => {
                // From the row: the reader's, and claimed. Given to somebody else
                // since, it is theirs to start (`taskWords.ts` `heldAs`).
                void run(() => start.mutateAsync(moveOf(task)), {
                  label: movedLabel({ taskRef: task.ref, move: 'start', landed: null }),
                  before: stand(),
                })
              }}
            >
              {start.isPending ? 'Working…' : primary.label}
            </Button>
          ) : (
            <ActionDialog
              action={primary.action}
              label={primary.label}
              title={primary.title}
              submitLabel={primary.label}
              size="sm"
              variant="primary"
              {...moveWindowOf(task)}
              disabled={pending}
            />
          )}
          {/*
            "Assign to me" is for a task **Claim cannot take** — a board card offering
            both was two words for one call, which is what the owner counted on
            17 September. Where Claim is offered it is the one-click take, on the list
            and on the board alike.
          */}
          {takeable && primary?.kind !== 'claim' ? (
            <Button
              size="sm"
              disabled={pending}
              onClick={() => {
                void run(take, {
                  label: movedLabel({ taskRef: task.ref, move: 'claim', landed: null }),
                  before: stand(),
                })
              }}
            >
              Assign to me
            </Button>
          ) : null}
          {startable ? (
            <Button
              size="sm"
              disabled={pending}
              onClick={() => {
                void run(
                  async () => {
                    await take()
                    /*
                     * From what the take has just made true — the reader's, and
                     * claimed. Said with nothing, it started whoever's task this was
                     * by then: a manager who reassigned it between the two steps had
                     * it started under their colleague's name by somebody else.
                     */
                    return unlessChangedBeforeStart(task.ref, () =>
                      start.mutateAsync({
                        task_ref: task.ref,
                        if_was: heldAs({
                          assignee_kind: 'user',
                          assignee_id: meId,
                          status: 'claimed',
                        }),
                      }),
                    )
                  },
                  {
                    /* Two moves, one press — one sentence, and Undo puts both back. */
                    label: `${task.ref} is yours and under way.`,
                    before: stand(),
                  },
                )
              }}
            >
              Claim &amp; start
            </Button>
          ) : null}
        </span>
        {/*
          More is **last**, and on the list it is pushed to the right edge of the Who
          cell.

          It used to sit third of four: an assigned row drew "More" alone and read
          right, but an unassigned one drew "Claim · More · Assign to me & start" and
          More landed in the middle of the strip, so the column had no edge to scan
          down. The order is fixed here because it is the same on a row and on a
          card; the push is `portal.css`'s and is scoped to `.tlist__who`, because a
          card's strip lives among the `.tcard__meta` chips and pushing it to the
          card's edge would tear More away from the primary move it belongs beside.
        */}
        <span className="trow-actions__more">
          <TaskMoreMenu task={task} size="sm" disabled={pending} />
        </span>
      </div>

      {/*
        A refusal is the page's line now — one place, one sentence, and it does not
        leave a red block hanging inside a table cell. What stays here is the one
        answer the notice cannot carry: a `$` call that became a review task, which
        is a **success** that did not happen and links the task it raised.
      */}
      <Outcome result={result ?? claim.data ?? start.data} />
    </div>
  )
}

function Outcome({ result }: { readonly result: unknown }) {
  const review = reviewOf(result)
  if (review === null) return null
  return <NeedsReviewNotice review={review} />
}

/** Where a task stood before a take — what Undo puts back. */
type TaskStand = {
  readonly status: TaskView['status']
  readonly assigneeKind: TaskView['assignee_kind']
  readonly assigneeId: string | null
}

/**
 * One primary move from the machine. Claim is only for **unassigned** open
 * work — an assigned open task is claimed from the detail page / Assign, not
 * from a Claim button beside the name. Prefer Send for review when the task
 * needs a person; otherwise Complete. Start is a plain button.
 */
function primaryOf(
  task: TaskView,
  events: readonly TaskEvent[],
  viewerId: string | null,
): PrimaryKind | null {
  /*
   * Claim is offered for an unassigned task **and for one already yours**.
   *
   * `tasks.claim` allows both — `service.ts` lets it through when the row has no
   * assignee *or* when the caller already holds it — and the row used to offer it
   * only for the first. So a task somebody had given you sat there with nothing but
   * More on it, and starting it meant opening the detail page: five steps for a move
   * the server does in one.
   */
  const yours = task.assignee_kind === 'user' && task.assignee_id === viewerId
  if (events.includes('claim') && (task.assignee_id === null || yours)) {
    /*
     * One move, **one word**, and it is `taskWords`'. This returned "Pick it up" for
     * a task already yours — a second name for the same call, which read as a second
     * move — and the question it was written to answer ("why Claim when it is already
     * mine?") is answered by the machine instead: `claim` takes the *lease*, `open`
     * offers nothing else, and `start` is not legal until it has run.
     */
    return { kind: 'claim', label: moveLabel('claim') }
  }
  if (events.includes('start')) return { kind: 'start', label: 'Start' }
  if (events.includes('submit_for_review') && task.needs_review) {
    return {
      kind: 'dialog',
      action: 'tasks_submit_for_review',
      label: 'Send for review',
      title: 'It will wait in review until a person approves and closes it',
    }
  }
  if (events.includes('complete')) {
    return {
      kind: 'dialog',
      action: 'tasks_complete',
      label: 'Mark done',
      title: 'Close it now — nobody else reviews it first',
    }
  }
  if (events.includes('submit_for_review')) {
    return {
      kind: 'dialog',
      action: 'tasks_submit_for_review',
      label: 'Send for review',
      title: 'It will wait in review until a person approves and closes it',
    }
  }
  /*
   * Not for a prepared `$` call nobody has made: closing that review does not run the
   * call, and the detail page's `ReviewPanel` is where it is read and made. A
   * one-click close from the list would record the decision with the spend never
   * happening — and since a held call is made from a task in review and from no
   * other, never able to happen. `holdsUnmadeCall` is the Status cell's rule too.
   */
  if (events.includes('approve_review') && !holdsUnmadeCall(task)) {
    return {
      kind: 'dialog',
      action: 'tasks_complete',
      label: 'Approve and close',
      title: 'Close the review — the work is done',
    }
  }
  return null
}
